import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Rx';
import * as deepExtend from 'deep-extend';
import { PathService } from './path.service';
import { AppStateService } from './app-state.service';
import { IDataSource } from './IDataSource';

type DataType = 'essential' | 'detail';

/**
 * Requests and caches data from two sources:
 *   - the small, essential json database
 *   - the large, detail json database
 *
 * In order to access all available data, both files have to be requested and cached.
 */
@Injectable()
export class DataProviderService {
    /**
     * When one data file is being received or has been received, then this object shall
     * contain an Observable for the corresponding data type.
     */
    private requestingData$: { [type: string]: Observable<IDataSource> } = {};
    /** This observable will be available once both data requesting observables have completed. */
    private wholeData$: Observable<IDataSource>;
    private cachedDb: IDataSource;

    public get isEssentialDataAvailable(): boolean {
        return this.requestingData$['essential'] !== undefined;
    }

    public get isDetailDataAvailable(): boolean {
        return this.requestingData$['detail'] !== undefined;
    }

    constructor(
        private http: HttpClient,
        private appState: AppStateService,
        private pathService: PathService
    ) {}

    public getData(withDetails: boolean): Observable<IDataSource> {
        if (withDetails)
            return this.getDetailData();
        else
            return this.getEssentialData();
    }

    private getEssentialData(): Observable<IDataSource> {
        return this.getJsonFile('essential');
    }

    /**
     * Returns a json database containing essential and detailed data combined.
     */
    private getDetailData(): Observable<IDataSource> {
        if (this.wholeData$ !== undefined)
            return this.wholeData$;

        const essentialData$ = this.getJsonFile('essential');
        const detailData$ = this.getJsonFile('detail');

        this.wholeData$ =
            Observable.zip(essentialData$, detailData$, (essentialData: IDataSource, detailData: IDataSource) =>
                deepExtend(essentialData, detailData))
            .shareReplay();

        this.requestingData$['detail'] = this.wholeData$;
        return this.wholeData$;
    }

    private getJsonFile(dataType: DataType): Observable<IDataSource> {
        const alreadyExistingObservable = this.requestingData$[dataType];
        if (alreadyExistingObservable)
            return alreadyExistingObservable;

        const requestUrl = this.pathService.dataPath(this.appState.languageId, dataType === 'essential');
        const newObservable = this.http.get<IDataSource>(requestUrl)
            .catch((error: any) => {
                const errorMsg = error.json().error || 'Server error';
                console.error(errorMsg);

                return Observable.throw(errorMsg);
            })
            .shareReplay();

        this.requestingData$[dataType] = newObservable;
        return newObservable;
    }
}



// WEBPACK FOOTER //
// ./src/app/core/data-provider.service.ts